import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { generateMessage } from '../../../services/communicationAIService';

const NewMessageModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    recipient: '',
    channel: 'whatsapp',
    messageType: 'general',
    context: '',
    tone: 'professional',
  });
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const channelOptions = [
    { value: 'whatsapp', label: 'واتساب' },
    { value: 'sms', label: 'رسالة نصية' },
    { value: 'email', label: 'بريد إلكتروني' },
  ];

  const messageTypeOptions = [
    { value: 'general', label: 'رسالة عامة' },
    { value: 'property_inquiry', label: 'استفسار عقار' },
    { value: 'viewing_confirmation', label: 'تأكيد معاينة' },
    { value: 'follow_up', label: 'متابعة' },
    { value: 'offer_notification', label: 'إشعار عرض' },
  ];

  const toneOptions = [
    { value: 'professional', label: 'رسمي' },
    { value: 'friendly', label: 'ودي' },
    { value: 'urgent', label: 'عاجل' },
  ];

  const handleGenerateMessage = async () => {
    if (!formData?.recipient || !formData?.context) {
      alert('الرجاء إدخال اسم المستلم والسياق');
      return;
    }

    setIsGenerating(true);
    try {
      const message = await generateMessage({
        recipientName: formData?.recipient,
        messageType: formData?.channel,
        context: formData?.context,
        tone: formData?.tone,
        language: 'Arabic',
      });
      setGeneratedMessage(message);
    } catch (error) {
      alert(error?.message || 'فشل في توليد الرسالة');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!generatedMessage) {
      alert('الرجاء توليد رسالة أولاً');
      return;
    }

    setIsSending(true);
    try {
      // Simulate sending
      await new Promise(resolve => setTimeout(resolve, 1500));
      alert('تم إرسال الرسالة بنجاح!');
      onClose();
    } catch (error) {
      alert('فشل في إرسال الرسالة');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="رسالة جديدة" 
      size="lg"
      footer={null}
      icon={null}
      iconColor={null}
    >
      <div className="space-y-6" dir="rtl">
        {/* Recipient */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            المستلم
          </label>
          <Input
            value={formData?.recipient}
            onChange={(e) => setFormData({ ...formData, recipient: e?.target?.value })}
            placeholder="اسم العميل"
          />
        </div>

        {/* Channel */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            قناة الاتصال
          </label>
          <Select
            value={formData?.channel}
            onChange={(e) => setFormData({ ...formData, channel: e?.target?.value })}
            options={channelOptions}
          />
        </div>

        {/* Message Type */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            نوع الرسالة
          </label>
          <Select
            value={formData?.messageType}
            onChange={(e) => setFormData({ ...formData, messageType: e?.target?.value })}
            options={messageTypeOptions}
          />
        </div>

        {/* Tone */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            نبرة الرسالة
          </label>
          <Select
            value={formData?.tone}
            onChange={(e) => setFormData({ ...formData, tone: e?.target?.value })}
            options={toneOptions}
          />
        </div>

        {/* Context */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            السياق / التفاصيل
          </label>
          <textarea
            value={formData?.context}
            onChange={(e) => setFormData({ ...formData, context: e?.target?.value })}
            placeholder="أدخل تفاصيل الرسالة أو السياق..."
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
            rows={4}
          />
        </div>

        {/* Generate Button */}
        <Button
          onClick={handleGenerateMessage}
          disabled={isGenerating || !formData?.recipient || !formData?.context}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader" size={18} className="animate-spin" />
              <span>جاري التوليد...</span>
            </>
          ) : (
            <>
              <Icon name="Sparkles" size={18} />
              <span>توليد رسالة بالذكاء الاصطناعي</span>
            </>
          )}
        </Button>

        {/* Generated Message */}
        {generatedMessage && (
          <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="MessageSquare" size={18} color="#3B82F6" />
              <span className="text-sm font-medium text-blue-400">الرسالة المولدة</span>
            </div>
            <textarea
              value={generatedMessage}
              onChange={(e) => setGeneratedMessage(e?.target?.value)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
              rows={5}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleSendMessage}
            disabled={!generatedMessage || isSending}
            className="flex-1"
            variant="primary"
          >
            {isSending ? (
              <>
                <Icon name="Loader" size={18} className="animate-spin" />
                <span>جاري الإرسال...</span>
              </>
            ) : (
              <>
                <Icon name="Send" size={18} />
                <span>إرسال الرسالة</span>
              </>
            )}
          </Button>
          <Button onClick={onClose} variant="secondary">
            إلغاء
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NewMessageModal;